import React from "react";
import classNames from "classnames";
import Text from "@ui/Text";
import ButtonIcon from "@ui/ButtonIconV2";
import { CloseLine, InformationLine } from "@ui/IconV2/react";
import { colors, spacings } from "@ui/Theme";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import t from "../../../../../../../../../i18n";
import { type FaultEvent } from "../../../../../../../../../api/useFaultEvents";
import SidePanelDetails from "./sidePanelDetails";
import SidePanelHistory from "./sidePanelHistory";
import type { Project } from "../../../../../../../../../api/useProjects";
import SidePanel from "../../../../components/SidePanel";
import { css } from "@linaria/core";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import type { SelectedSidePanelFaultEvent } from "../../../../../../hooks/createGlobalStates/useSelectedSidePanelFaultEvent";
import { useEventHistories } from "../../../../../../../../../api/events/histories";

interface Props {
  project: Project;
  faultEvent: FaultEvent;
  tab: "fault" | "history";
  isFolded: boolean;
  setTab: (v: React.SetStateAction<SelectedSidePanelFaultEvent>) => void;
  closePanel: () => void;
}

const Details = ({ isFolded, project, faultEvent, closePanel, tab, setTab }: Props) => {
  const eventHistories = useEventHistories({
    projectId: project.id,
    faultEventId: faultEvent.id,
    failureNames: faultEvent.metadata.faults.map(fault => fault.name),
  });

  const faultEventsHistory = React.useMemo(
    () => eventHistories.filter(group => group.kind === "fault"),
    [eventHistories],
  );

  const faultEventsHistoryCount = React.useMemo(
    () => faultEventsHistory.reduce((acc, { events }) => acc + events.length, 0),
    [faultEventsHistory],
  );

  const handleTabClick = React.useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    e => {
      if (e.target instanceof HTMLElement && (e.target.id === "fault" || e.target.id === "history"))
        setTab({
          type: "details",
          tab: e.target.id,
        });
    },
    [setTab],
  );

  return (
    <SidePanel
      isFolded={isFolded}
      header={
        <>
          <div className={titleSidePanel}>
            <Text type="h4">{t("project.fault_event.side_panel.title")}</Text>
            <TooltipV2
              placement="top"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={t("project.fault_event.side_panel.details.hitory.tooltip.info")}
                />
              }
            >
              <span className={centerIcon}>
                <InformationLine size="m" color={colors.carbon800} />
              </span>
            </TooltipV2>
          </div>
          <ButtonIcon onClick={closePanel}>
            <CloseLine size="m" color={colors.carbon800} />
          </ButtonIcon>
          <NavV2>
            <Tab
              id="fault"
              onClick={handleTabClick}
              className={classNames({ active: tab === "fault" })}
            >
              {t("project.fault_event.side_panel.tab.fault", faultEvent.metadata.faults.length)}
            </Tab>
            <Tab
              id="history"
              onClick={handleTabClick}
              className={classNames({ active: tab === "history" })}
            >
              {t("project.event.side_panel.tab.history", faultEventsHistoryCount)}
            </Tab>
          </NavV2>
        </>
      }
    >
      {tab === "fault" ? (
        <SidePanelDetails projectId={project.id} faultEvent={faultEvent} />
      ) : (
        <></>
      )}
      {tab === "history" ? (
        <SidePanelHistory
          project={project}
          faultEvent={faultEvent}
          faultEventsHistory={faultEventsHistory}
        />
      ) : (
        <></>
      )}
    </SidePanel>
  );
};

export default Details;

//#region CSS
const titleSidePanel = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;
const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const styledOverlayWrapper = css`
  max-width: 370px;
`;
//#endregion CSS
